import React, { useRef, useEffect } from 'react';
import Webcam from 'react-webcam';
import { storage, db } from '../../../firebase/config';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

interface ProctorCameraProps {
  studentId: string;
  examId: string;
  moduleId: string;
  enabled: boolean;
}

const CAPTURE_INTERVAL_MS = 300000; // 5 minutes

const ProctorCamera: React.FC<ProctorCameraProps> = ({ studentId, examId, moduleId, enabled }) => {
  const webcamRef = useRef<Webcam>(null);

  useEffect(() => {
    if (!enabled || !studentId || !examId || !moduleId) return;

    const capture = async () => {
      if (webcamRef.current) {
        try {
          const imageSrc = webcamRef.current.getScreenshot();
          if (imageSrc) {
            const storageRef = ref(
              storage,
              `proctor_snaps/${studentId}/${Date.now()}.jpg`
            );
            await uploadString(storageRef, imageSrc, 'data_url');
            const imageUrl = await getDownloadURL(storageRef);

            const examLogsRef = collection(
              db,
              'modules',
              moduleId,
              'tests',
              examId,
              'submissions',
              studentId,
              'exam-logs'
            );
            await addDoc(examLogsRef, {
              type: 'PROCTOR_SNAPSHOT',
              detail: 'Periodic webcam snapshot',
              timestamp: serverTimestamp(),
              imageUrl
            });
          }
        } catch (err) {
          console.error('Failed to capture/upload webcam snapshot:', err);
        }
      }
    };

    const initialTimeout = setTimeout(capture, 3000);
    const interval = setInterval(capture, CAPTURE_INTERVAL_MS);
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [studentId, examId, moduleId, enabled]);

  if (!enabled) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 10,
        right: 10,
        opacity: 0.3,
        zIndex: 1000
      }}
    >
      <Webcam
        audio={false}
        ref={webcamRef}
        screenshotFormat="image/jpeg"
        width={120}
        videoConstraints={{ facingMode: 'user' }}
      />
    </div>
  );
};

export default ProctorCamera;
